import React, { useState, useReducer, useRef } from 'react'
import { clinicIndexToPageNum } from './ResultsList/resultsUtils.js'
import { updateSearchParams } from '../search/updateSearchParams.js'
import ClinicFinderForm from './ClinicFinderForm/ClinicFinderForm.jsx'
import { LoadinDots } from '../shared_components/loadin_dots/LoadinDots.jsx'
import FilterRibbon from './FilterRibbon/FilterRibbon.jsx'
import ResultsList from './ResultsList/ResultsList.jsx'
import ClinicMap from './ClinicMap/ClinicMap.jsx'
import ResultsSwipeableList from './ResultsSwipeableList/ResultsSwipeableList.jsx'
import './ClinicFinderResults.scss'

const ClinicFinderResults = ({
  searchTerms,
  updateSearchTerms,
  onSearch,
  navigateResponse,
  lexicon,
  googleMapsApiKey,
}) => {
  const [isCondensed, setIsCondensed] = useState(true)
  const sliderRef = useRef()
  const initialURLParams = new URLSearchParams(window.location.search)
  const queryPageNum = parseInt(initialURLParams.get('page')) || 1

  const [searchedLocation, setSearchedLocation] = useState(searchTerms.location) // cache the thing we've actually searched for

  const [cardDisplayState, updateCardDisplayState] = useReducer(
    (oldState, stateUpdate) => ({
      ...oldState,
      ...{ activePageNum: queryPageNum },
      ...stateUpdate,
    }),
    {
      hoveringClinicIndex: -1,
      selectedClinicIndex: null,
      focusedPinIndex: null,
      isMobileMapOpen: false,
      activePageNum: queryPageNum,
    }
  )

  const toggleMobileMap = () => {
    // toggle the map open/closed
    const newDisplayState = {
      isMobileMapOpen: !cardDisplayState.isMobileMapOpen,
    }

    // ON MAP CLOSE: change the list to the page of the card we were looking at
    if (cardDisplayState.isMobileMapOpen) {
      const newPageNum = clinicIndexToPageNum(
        cardDisplayState.selectedClinicIndex
      )
      newDisplayState.activePageNum = newPageNum
      updateSearchParams({ page: newPageNum })
    }

    updateCardDisplayState(newDisplayState)
  }

  const handleSearchSubmit = () => {
    setIsCondensed(true)
    setSearchedLocation(searchTerms.location)
    onSearch()
  }

  let sortedAndFilteredClinics = navigateResponse
    ? navigateResponse.clinics
    : []

  // filter out telehealth-only clinics
  sortedAndFilteredClinics =
    sortedAndFilteredClinics &&
    sortedAndFilteredClinics.filter(
      (clinic) => clinic.location_type !== 'telehealth_only'
    )

  const hasValidClinicResponse =
    navigateResponse && 'clinics' in navigateResponse
  const hasValidErrorCode = navigateResponse && 'error' in navigateResponse

  return (
    <div
      className={`ClinicFinderResults ${
        cardDisplayState.isMobileMapOpen ? 'map-open' : 'map-closed'
      }`}
    >
      <div className="header-and-search-container">
        <h2>{lexicon.get('header')}</h2>
        <div className="subheader">{lexicon.get('subheader')}</div>

        <ClinicFinderForm
          searchTerms={searchTerms}
          updateSearchTerms={updateSearchTerms}
          onSearch={handleSearchSubmit}
          isCondensed={isCondensed}
          setIsCondensed={setIsCondensed}
          showTitle={false}
        />
      </div>

      {/* eslint-disable-next-line no-nested-ternary -- jsx use-case ok */}
      {hasValidClinicResponse && navigateResponse.clinics.length > 0 ? (
        <div className="results-and-map">
          <div className="scrollable-area">
            <div className="sticky-header">
              <FilterRibbon
                lexicon={lexicon.subset('FilterRibbon')}
                searchTerms={searchTerms}
                updateSearchTerms={updateSearchTerms}
                isMobileMapOpen={cardDisplayState.isMobileMapOpen}
                toggleMobileMap={toggleMobileMap}
              />
            </div>

            <ResultsList
              lexicon={lexicon}
              sortedAndFilteredClinics={sortedAndFilteredClinics}
              cardDisplayState={cardDisplayState}
              updateCardDisplayState={updateCardDisplayState}
            />
          </div>

          {sortedAndFilteredClinics.length > 0 && (
            <ClinicMap
              lexicon={lexicon}
              sortedAndFilteredClinics={sortedAndFilteredClinics}
              cardDisplayState={cardDisplayState}
              updateCardDisplayState={updateCardDisplayState}
              googleMapsApiKey={googleMapsApiKey}
              sliderRef={sliderRef}
            />
          )}

          <ResultsSwipeableList
            lexicon={lexicon}
            sortedAndFilteredClinics={sortedAndFilteredClinics}
            cardDisplayState={cardDisplayState}
            updateCardDisplayState={updateCardDisplayState}
            sliderRef={sliderRef}
          />
        </div>
      ) : // eslint-disable-next-line no-nested-ternary
      hasValidClinicResponse ? (
        <div className="loading-container no-clinics">
          {lexicon.get('error_term', { search_term: searchedLocation })}
          <br />
          {lexicon.get('error_try')}
        </div>
      ) : hasValidErrorCode ? (
        <div className="loading-container error">
          {lexicon.get('error_server')}
        </div>
      ) : (
        <div className="loading-container">
          <LoadinDots />
        </div>
      )}
    </div>
  )
}

export default ClinicFinderResults
